import { getBusinessProfile, getStaffProfile } from '@services/profileService';
import type { User } from '@app-types/models/user';

export const getUser = async (): Promise<User> => {
  let user: User | null = null;

  // Check for impersonated user first (stored in sessionStorage)
  const impersonatedUserString = sessionStorage.getItem('impersonated_user');
  if (impersonatedUserString) {
    try {
      user = JSON.parse(impersonatedUserString) as User;
    } catch (error) {
      console.error("Failed to parse impersonated user data:", error);
    }
  }

  // Fall back to normal user from localStorage
  if (!user) {
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        user = JSON.parse(userString) as User;
      } catch (error) {
        console.error("Failed to parse user data from localStorage:", error);
      }
    }
  }

  const isImpersonatingUser = !!sessionStorage.getItem('impersonation_token');

  if (user?.role === 'business') {
    try {
      const profile = await getBusinessProfile();
      if (profile) {
        return profile as unknown as User;
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
    // Fall back to basic user data during impersonation
    if (isImpersonatingUser && user) {
      console.warn('Using basic user data for impersonation (profile API failed or returned null)');
      return user;
    }
    throw new Error('Failed to fetch business profile');
  } else {
    try {
      const profile = await getStaffProfile();
      if (profile) {
        return profile as unknown as User;
      }
    } catch (error) {
      console.error('Error fetching staff profile:', error);
    }
    // Fall back to basic user data during impersonation
    if (isImpersonatingUser && user) {
      console.warn('Using basic user data for impersonation (profile API failed or returned null)');
      return user;
    }
    throw new Error('Failed to fetch staff profile');
  }
};

/**
 * Check if currently impersonating a user
 * @returns Whether impersonation is active
 */
export const isImpersonating = (): boolean => {
  return !!sessionStorage.getItem('impersonation_token');
};

/**
 * Get the original user (before impersonation)
 * @returns The original user or null
 */
export const getOriginalUser = (): User | null => {
  const originalUserString = sessionStorage.getItem('godmode_original_user');
  if (originalUserString) {
    try {
      return JSON.parse(originalUserString) as User;
    } catch (error) {
      console.error("Failed to parse original user data:", error);
    }
  }
  return null;
};
